import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Mail, User, MessageSquare, CheckCircle2, Building2, MapPin } from 'lucide-react';
import AppNav from '@/components/AppNav';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/support/contact`, { name, email, message });
      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch {
      toast.error('Failed to send message. Please email hey@poettik.com directly.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with the Poettik team. Account help, billing questions, press or partnership enquiries, we're happy to hear from you." />
      <nav className="relative z-50 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
          <AppNav />
        </div>
      </nav>

      <main className="relative z-0 flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3">Contact us</h1>
          <p className="text-muted-foreground">
            Questions about your account, billing, a partnership or the press? Send us a message
            and a real person will get back to you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-start">
          {/* Company details */}
          <Card data-testid="contact-company-card">
            <CardContent className="p-7 space-y-6 text-center md:text-left">
              <div>
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-primary">
                  <Mail className="w-5 h-5" />
                  <h2 className="font-semibold text-foreground">Email us</h2>
                </div>
                <a href="mailto:hey@poettik.com" className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="contact-email-link">
                  hey@poettik.com
                </a>
              </div>

              <div>
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-primary">
                  <Building2 className="w-5 h-5" />
                  <h2 className="font-semibold text-foreground">Company</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed" data-testid="contact-company-details">
                  Poettik is a trading name of VZV Ltd, a company registered in
                  England and Wales.
                  <br />
                  Company No. 10689932
                </p>
              </div>

              <div>
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-primary">
                  <MapPin className="w-5 h-5" />
                  <h2 className="font-semibold text-foreground">Based in</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  England, United Kingdom
                </p>
              </div>

              <p className="text-xs text-muted-foreground/70 pt-2">
                Prefer self-serve? Our <Link to="/faq" className="underline underline-offset-2 hover:text-foreground">FAQ</Link> and{' '}
                <Link to="/support" className="underline underline-offset-2 hover:text-foreground">Support</Link> pages cover most questions.
              </p>
            </CardContent>
          </Card>

          {/* Contact form */}
          <Card data-testid="contact-form-card">
            <CardContent className="p-7">
              {sent ? (
                <div className="text-center py-10">
                  <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Message sent</h3>
                  <p className="text-sm text-muted-foreground mb-6">We'll get back to you as soon as possible.</p>
                  <Link to="/"><Button variant="outline" data-testid="contact-back-home">Back to home</Button></Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input className="pl-10" value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" data-testid="contact-name" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input type="email" className="pl-10" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>How can we help?</Label>
                    <Textarea className="min-h-[140px]" value={message} onChange={e => setMessage(e.target.value)} required placeholder="Tell us what's on your mind..." data-testid="contact-message" />
                  </div>
                  <Button type="submit" variant="premium" className="w-full" disabled={loading} data-testid="contact-submit">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                    Send message
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
